import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Mood as PersonIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { STATUS_COLORS } from "@/theme/theme";
import type { Persona } from "../types.ts";
import { clickableProps } from "../utils/keyboard";

interface PersonaCardProps {
  persona: Persona;
  onEdit: (persona: Persona) => void;
  onDelete: (persona: Persona) => void;
  onView: (persona: Persona) => void;
  onCopy: (persona: Persona) => void;
  disabled?: boolean;
  example?: boolean;
}

export default function PersonaCard({
  persona,
  onEdit,
  onDelete,
  onView,
  onCopy,
  disabled = false,
  example = false,
}: PersonaCardProps) {
  const [showFullPrompt, setShowFullPrompt] = useState(false);

  const truncatedPrompt =
    persona.system_prompt.length > 120
      ? `${persona.system_prompt.substring(0, 120)}...`
      : persona.system_prompt;

  return (
    <Tooltip
      title={example ? "This is an example persona, create your own!" : ""}
      arrow
      placement="right"
    >
      <Box
        sx={{
          cursor: disabled ? "help" : "pointer",
        }}
      >
        <Badge
          badgeContent={example ? "Example" : undefined}
          color="primary"
          invisible={!example}
          sx={{
            "& .MuiBadge-badge": {
              right: 20,
              top: 5,
            },
          }}
        >
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
              backgroundColor: "common.white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              "&:hover": disabled
                ? {}
                : {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                    borderColor: "primary.main",
                  },
            }}
          >
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Stack spacing={2.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        width: 48,
                        height: 48,
                        boxShadow: "0 2px 8px rgba(25, 118, 210, 0.2)",
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        component="h6"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          letterSpacing: "-0.02em",
                          mb: 0.5,
                        }}
                      >
                        {persona.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        {persona.user}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>

                {persona.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.5,
                      fontSize: "0.875rem",
                    }}
                  >
                    {persona.description}
                  </Typography>
                )}
                <Divider sx={{ borderColor: "grey.200" }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      mb: 1,
                      fontSize: "0.875rem",
                    }}
                  >
                    System Prompt
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "grey.200",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: STATUS_COLORS.default.bg,
                        borderColor: "divider",
                      },
                    }}
                    {...clickableProps(() => setShowFullPrompt(!showFullPrompt))}
                    onClick={() => setShowFullPrompt(!showFullPrompt)}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        color: "text.primary",
                        lineHeight: 1.4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {disabled || showFullPrompt ? persona.system_prompt : truncatedPrompt}
                    </Typography>
                  </Paper>
                  {!disabled && persona.system_prompt.length > 120 && (
                    <Button
                      size="small"
                      onClick={() => setShowFullPrompt(!showFullPrompt)}
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        fontWeight: 500,
                        "&:hover": {
                          backgroundColor: "grey.50",
                        },
                      }}
                    >
                      {showFullPrompt ? "Show Less" : "Show More"}
                    </Button>
                  )}
                </Box>
              </Stack>
            </CardContent>

            {!disabled && (
              <CardActions
                sx={{
                  justifyContent: "space-between",
                  p: 2,
                  borderTop: "1px solid",
                  borderTopColor: "grey.200",
                  backgroundColor: "background.default",
                }}
              >
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="View Details" arrow>
                    <IconButton
                      aria-label="View Details"
                      size="small"
                      onClick={() => onView(persona)}
                      disabled={disabled}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          backgroundColor: STATUS_COLORS.info.bg,
                          color: "primary.main",
                        },
                      }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy System Prompt" arrow>
                    <IconButton
                      aria-label="Copy System Prompt"
                      size="small"
                      onClick={() => onCopy(persona)}
                      disabled={disabled}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          backgroundColor: STATUS_COLORS.success.bg,
                          color: STATUS_COLORS.success.text,
                        },
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Edit Persona" arrow>
                    <IconButton
                      aria-label="Edit Persona"
                      size="small"
                      onClick={() => onEdit(persona)}
                      disabled={disabled}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          backgroundColor: STATUS_COLORS.warning.bg,
                          color: STATUS_COLORS.warning.text,
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Persona" arrow>
                    <IconButton
                      aria-label="Delete Persona"
                      size="small"
                      onClick={() => onDelete(persona)}
                      disabled={disabled}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          backgroundColor: STATUS_COLORS.error.bg,
                          color: STATUS_COLORS.error.text,
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardActions>
            )}
          </Card>
        </Badge>
      </Box>
    </Tooltip>
  );
}
